import React, { useState } from 'react';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { ManageUnitsDrawer } from './manage-units-drawer';
import { AsyncManageUnitsDrawer } from './async-manage-units-drawer';
import { AsyncResponsePlanProvider } from './store/async-response-plan-provider';

/**
 * Host for the Manage Units drawer. Forks between two entirely separate subtrees on
 * `asyncResponsePlansEnabled`:
 *
 *  - true → `<AsyncResponsePlanProvider>` wrapping `<AsyncManageUnitsDrawer>`, the async
 *    implementation (its own store hook, its own drawer data hook, its own drawer component).
 *  - false → `<ManageUnitsDrawer>`, byte-identical to `main`, with no async code mounted at all.
 *
 * The flag is frozen at drawer-open (via `useState(() => ...)`), not read live. `asyncResponsePlansEnabled`
 * comes from `EnabledFeaturesContext` and CAN change mid-session (a Preferences save or a
 * cross-tab feature refresh both call `setEnabledFeatures`). Reading it live here would let a
 * mid-session flip remount an already-open drawer from one implementation to the other,
 * discarding in-flight local state (selection, filters, in-flight async plan). Freezing at mount
 * means a dispatcher's open drawer always finishes its session on the implementation it started
 * with; a flag flip only takes effect the next time the drawer opens.
 */
export function ManageUnitsDrawerHost({ eventId }: { eventId: number }): React.ReactElement {
    const { asyncResponsePlansEnabled } = useEnabledFeatures();
    const [asyncOn] = useState(() => asyncResponsePlansEnabled);

    if (asyncOn) {
        return (
            <AsyncResponsePlanProvider eventId={eventId}>
                <AsyncManageUnitsDrawer eventId={eventId} />
            </AsyncResponsePlanProvider>
        );
    }

    return <ManageUnitsDrawer eventId={eventId} />;
}
